import { useRef, useCallback, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger);

const skillGroups = [
  {
    icon: '⚙️',
    title: 'Languages',
    skills: ['Python', 'JavaScript', 'HTML / CSS', 'SQL'],
  },
  {
    icon: '🖥️',
    title: 'Frameworks & Libraries',
    skills: ['React.js', 'n8n', 'TailwindCSS', 'Bootstrap'],
  },
  {
    icon: '🤖',
    title: 'AI & Automation',
    skills: ['Gen AI & Agentic AI', 'LLMs', 'AI Agents', 'Workflow Automation'],
  },
  {
    icon: '🛠️',
    title: 'Tools & Platforms',
    skills: ['Git & GitHub', 'VS Code', 'macOS / Linux', 'Formspree'],
  },
];

export default function Skills() {
  const sectionRef = useRef();
  const trackRef = useRef();
  const scrollTarget = useRef(0);
  const isHovered = useRef(false);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);
  const [cursor, setCursor] = useState('grab');

  /* Smooth scroll animation loop */
  useGSAP(() => {
    // Entrance animation on scroll into view
    gsap.fromTo(
      sectionRef.current,
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
          once: true,
        },
      }
    );
  }, { scope: sectionRef });

  /* Wheel → horizontal scroll (only when hovered, only when there is room) */
  const handleWheel = useCallback((e) => {
    if (!isHovered.current) return;

    const track = trackRef.current;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = scrollTarget.current <= 0;
    const atEnd   = scrollTarget.current >= maxScroll;

    // At left edge scrolling up → let page scroll up normally
    if (atStart && e.deltaY < 0) return;
    // At right edge scrolling down → let page scroll down normally
    if (atEnd && e.deltaY > 0) return;

    // In between → intercept and drive horizontal scroll
    e.preventDefault();
    scrollTarget.current = Math.max(0, Math.min(maxScroll, scrollTarget.current + e.deltaY * 1.2));

    gsap.to(track, {
      scrollLeft: scrollTarget.current,
      duration: 0.6,
      ease: 'power2.out',
      overwrite: true,
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    isHovered.current = true;
    window.addEventListener('wheel', handleWheel, { passive: false });
  }, [handleWheel]);

  const handleMouseLeave = useCallback(() => {
    isHovered.current = false;
    isDragging.current = false;
    setCursor('grab');
    window.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartScroll.current = scrollTarget.current;
    setCursor('grabbing');
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const track = trackRef.current;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const delta = dragStartX.current - e.clientX;
    scrollTarget.current = Math.max(0, Math.min(maxScroll, dragStartScroll.current + delta));
    gsap.to(track, {
      scrollLeft: scrollTarget.current,
      duration: 0.3,
      ease: 'power2.out',
      overwrite: true,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    setCursor('grab');
  }, []);

  return (
    <section
      id="skills"
      ref={sectionRef}
      style={{ padding: '8rem 0', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}
    >
      {/* Section header */}
      <div className="page-wrap">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '3rem', flexWrap: 'wrap', gap: '2rem' }}>
          <div>
            <span className="sec-num">02</span>
            <h2 className="display-lg" style={{ marginTop: '-1.5rem' }}>Skills</h2>
          </div>
          <p style={{ maxWidth: '40ch', color: 'var(--muted)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            A curated toolkit spanning the full spectrum — from pixel-perfect UIs to intelligent AI pipelines.
          </p>
        </div>
        <hr className="hr" style={{ marginBottom: '3rem' }} />
      </div>

      {/* Hover zone — intercepts wheel scroll */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ cursor, position: 'relative', userSelect: 'none' }}
      >
        {/* Scroll hint */}
        <p style={{
          textAlign: 'center',
          color: 'var(--muted)',
          fontSize: '0.72rem',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '1.25rem',
          opacity: 0.6,
        }}>
          ↕ Scroll · ← Drag →
        </p>

        {/* Card track — overflow hidden, GSAP drives scrollLeft */}
        <div
          ref={trackRef}
          style={{
            display: 'flex',
            gap: '1.5rem',
            overflowX: 'hidden',
            paddingLeft: 'calc((100vw - min(75rem, 100vw)) / 2 + 2rem)',
            paddingRight: 'calc((100vw - min(75rem, 100vw)) / 2 + 2rem)',
            paddingBottom: '2rem',
          }}
        >
          {skillGroups.map((group) => (
            <div
              key={group.title}
              className="card skill-card"
              style={{
                padding: '2.5rem',
                minWidth: '320px',
                maxWidth: '320px',
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: '2.5rem', marginBottom: '1.25rem' }}>{group.icon}</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', marginBottom: '1.25rem', color: 'var(--white)' }}>
                {group.title}
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {group.skills.map(s => (
                  <span key={s} className="tag skill-tag">{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
